import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Network,
  Pencil,
  Plus,
  Search,
  Star,
  Table as TableIcon,
  UserPlus,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import { cn } from '@/lib/utils'
import type { CapacityDay, ResourceSummary } from '@/api/resourceCenterApi'
import { useResourceCapacity, useResources, useShiftDefinitions } from '@/hooks/useResourceCenter'
import { WORK_TYPE_CHIPS, shiftSummary, splitLabel, splitSegments } from '@/lib/assignmentGroups'
import {
  SLIDER_WEEKS_AHEAD,
  SLIDER_WEEKS_BACK,
  addDays,
  cellView,
  dayHeader,
  hoursLabel,
  todayIso,
  windowDates,
  windowLabel,
  windowStart,
} from '@/lib/resourceCalendar'
import { ABSENCE_STRIPES, WORK_COLOR } from './profile/SplitSection'
import { FORM_INPUT } from './listStyles'
import { AddPeopleDialog } from './AddPeopleDialog'
import { PersonGroupsDialog } from './PersonGroupsDialog'
import { PeopleSchedule } from './PeopleSchedule'
import { PERMISSIONS, usePermission } from '@/lib/permissions'

// The profile columns are the system table kit (resizable, hideable, saved per
// user). The day columns after them are the date matrix, governed by the slider
// rather than the Columns menu — the same carve-out as ResourceCapacityGrid.
const TABLE_ID = 'resources-people'
const COLUMNS: TableColumnDef[] = [
  { id: 'name', header: 'Resource name', locked: true, defaultWidth: 240, sortable: true },
  { id: 'supervisor', header: 'Supervisor', defaultWidth: 150, sortable: true },
  // New ids: 'groups' used to be the (hidden) primary-group "Team" column, and a
  // saved preference must not hide the real Groups column (z8nrz7f7dk).
  { id: 'teams', header: 'Team', defaultWidth: 140, sortable: true },
  { id: 'groupChips', header: 'Groups', defaultWidth: 190, sortable: true },
  { id: 'work', header: 'Work types', defaultWidth: 130 },
  { id: 'split', header: '% PR·RV·QA·SA', defaultWidth: 130 },
  { id: 'shift', header: 'Shift', defaultWidth: 170, sortable: true },
  { id: 'labRole', header: 'Lab role', defaultWidth: 100, sortable: true, defaultHidden: true },
  { id: 'email', header: 'Email', defaultWidth: 200, sortable: true, defaultHidden: true },
]

const SORT: SortAccessors<ResourceSummary> = {
  name: r => r.name,
  supervisor: r => r.supervisor?.name ?? null,
  teams: r => r.teams[0]?.name ?? null,
  groupChips: r => r.groups[0]?.name ?? null,
  shift: r => shiftSummary(r.shift)?.title ?? null,
  labRole: r => (r.isSupervisor ? 0 : 1),
  email: r => r.email,
}

const LEGEND = [
  { label: 'Process (PR)', className: WORK_COLOR.PROCESS },
  { label: 'Review (RV)', className: WORK_COLOR.REVIEW },
  { label: 'QA', className: WORK_COLOR.QA },
  { label: 'Support (SA)', className: WORK_COLOR.SUPPORT },
  { label: 'Dynamic', className: WORK_COLOR.FLEX },
]

/**
 * The People tab — the deck's "Profile & Calendar in One Grid" (slides 7–8):
 * every member of the selected center from `GET /resource-profiles`, with two
 * weeks of daily capacity from `GET /resource-capacity`. The slider moves the
 * window; a name opens that person's profile page (slides 4 and 9).
 */
export function ResourcesList() {
  const today = todayIso()
  const resources = useResources()
  const [weekOffset, setWeekOffset] = useState(0)
  const start = windowStart(today, weekOffset)
  const dates = windowDates(start)
  // From the day BEFORE the window: the Day timeline draws an overnight shift's
  // tail on the next morning, and the window's first morning needs the night
  // before. The table and roster only read the window's own dates.
  const capacity = useResourceCapacity(addDays(dates[0], -1), dates[dates.length - 1])
  const [search, setSearch] = useState('')
  const [adding, setAdding] = useState(false)
  const navigate = useNavigate()
  const { can, isSuperAdmin } = usePermission()
  // Adding a member is POST /tenants/:id/members(/by-email) — users.manage.
  const canAddPeople = isSuperAdmin || can(PERMISSIONS.usersManage)
  // Group membership is a resource-profile write.
  const canManageProfiles = can(PERMISSIONS.resourceCapabilitiesManage)
  const [groupsFor, setGroupsFor] = useState<ResourceSummary | null>(null)
  // Table: profile columns + daily capacity. Schedule: who works when, and the times.
  const [view, setView] = useState<'table' | 'schedule'>('table')
  const shifts = useShiftDefinitions()

  // Team filter (z8nrz7f7dk): a team pill above the table, or a team chip in a row.
  const [pickedTeam, setTeamId] = useState<string | null>(null)

  const all = resources.data ?? []
  // Every team someone here is in, with how many of them.
  const teamCounts = useMemo(() => {
    const m = new Map<string, { id: string; name: string; count: number }>()
    for (const r of all)
      for (const t of r.teams) m.set(t.id, { ...t, count: (m.get(t.id)?.count ?? 0) + 1 })
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name))
  }, [all])
  const noTeam = all.filter(r => r.teams.length === 0).length
  // A picked team that no longer has anyone (moved out in the groups popup)
  // falls back to All, so the filter can never strand the table empty.
  const teamId =
    pickedTeam === NO_TEAM
      ? noTeam > 0
        ? NO_TEAM
        : null
      : pickedTeam !== null && teamCounts.some(t => t.id === pickedTeam)
        ? pickedTeam
        : null
  const needle = search.trim().toLowerCase()
  const filtered = useMemo(
    () =>
      all.filter(
        r =>
          (teamId === null ||
            (teamId === NO_TEAM ? r.teams.length === 0 : r.teams.some(t => t.id === teamId))) &&
          (needle === '' ||
            [r.name, r.email, r.supervisor?.name, ...r.teams.map(t => t.name), ...r.groups.map(g => g.name)].some(v =>
              v?.toLowerCase().includes(needle),
            )),
      ),
    [all, needle, teamId],
  )
  const cols = useTableColumns(TABLE_ID, COLUMNS)
  const rows = useSortedRows(filtered, cols.sort, SORT)
  const daysOf = useMemo(() => new Map((capacity.data ?? []).map(c => [c.userId, c.days])), [capacity.data])

  const open = (id: string) => navigate(`/resources/people/${id}`)

  return (
    <div className='overflow-hidden rounded-2xl border bg-background shadow-sm'>
        <div className='flex flex-wrap items-center gap-3 px-4 pb-3 pt-4'>
          <div className='relative w-80'>
            <Search className='pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground' />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder='Search people, teams, groups, supervisors'
              aria-label='Search people'
              className={cn(FORM_INPUT, 'h-9 rounded-full bg-muted/40 pl-9')}
            />
          </div>
          <span className='text-sm text-muted-foreground'>
            <span className='font-semibold text-foreground tabular-nums'>{rows.length}</span>
            {rows.length !== all.length && <> of {all.length}</>} {all.length === 1 ? 'person' : 'people'}
          </span>
          <div role='tablist' aria-label='People view' className='ml-auto inline-flex rounded-full bg-muted p-1'>
            {(
              [
                { value: 'table', label: 'Table', icon: TableIcon },
                { value: 'schedule', label: 'Schedule', icon: CalendarClock },
              ] as const
            ).map(v => (
              <button
                key={v.value}
                type='button'
                role='tab'
                aria-selected={view === v.value}
                onClick={() => setView(v.value)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-all',
                  view === v.value ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <v.icon className='h-3.5 w-3.5' /> {v.label}
              </button>
            ))}
          </div>
          {canAddPeople && (
            <Button size='sm' className='h-9 rounded-full px-4' onClick={() => setAdding(true)}>
              <UserPlus className='mr-1.5 h-4 w-4' /> Add people
            </Button>
          )}
        </div>

        {teamCounts.length > 0 && (
          <div role='radiogroup' aria-label='Filter by team' className='flex flex-wrap items-center gap-1.5 px-4 pb-3'>
            <TeamPill active={teamId === null} onClick={() => setTeamId(null)} label='All teams' count={all.length} />
            {teamCounts.map(t => (
              <TeamPill
                key={t.id}
                active={teamId === t.id}
                onClick={() => setTeamId(teamId === t.id ? null : t.id)}
                label={t.name}
                count={t.count}
                icon
              />
            ))}
            {noTeam > 0 && (
              <TeamPill
                active={teamId === NO_TEAM}
                onClick={() => setTeamId(teamId === NO_TEAM ? null : NO_TEAM)}
                label='No team'
                count={noTeam}
              />
            )}
          </div>
        )}

        <div className='flex flex-wrap items-center gap-3 border-y bg-muted/30 px-4 py-2'>
          <div className='hidden flex-wrap items-center gap-3 text-xs text-muted-foreground lg:flex'>
            {LEGEND.map(l => (
              <span key={l.label} className='flex items-center gap-1.5'>
                <span className={cn('h-2 w-2 rounded-full', l.className)} /> {l.label}
              </span>
            ))}
            <span className='flex items-center gap-1.5'>
              <span className='h-2.5 w-2.5 rounded-sm border' style={{ backgroundImage: ABSENCE_STRIPES }} /> Unavailable
            </span>
          </div>
          <div className='ml-auto flex items-center gap-2'>
            <span className='text-xs font-medium text-muted-foreground'>Calendar</span>
            <Button size='icon' variant='ghost' className='h-8 w-8' aria-label='Previous week' disabled={weekOffset <= -SLIDER_WEEKS_BACK} onClick={() => setWeekOffset(weekOffset - 1)}>
              <ChevronLeft className='h-4 w-4' />
            </Button>
            <input
              type='range'
              min={-SLIDER_WEEKS_BACK}
              max={SLIDER_WEEKS_AHEAD}
              value={weekOffset}
              onChange={e => setWeekOffset(Number(e.target.value))}
              aria-label='Move the calendar window'
              className='w-40 accent-primary'
            />
            <Button size='icon' variant='ghost' className='h-8 w-8' aria-label='Next week' disabled={weekOffset >= SLIDER_WEEKS_AHEAD} onClick={() => setWeekOffset(weekOffset + 1)}>
              <ChevronRight className='h-4 w-4' />
            </Button>
            <span className='w-28 text-sm font-semibold tabular-nums'>{windowLabel(start)}</span>
            <Button size='sm' variant='outline' className='h-8' disabled={weekOffset === 0} onClick={() => setWeekOffset(0)}>
              Today
            </Button>
          </div>
        </div>

        {(resources.error || capacity.error) && (
          <p className='px-4 py-3 text-sm text-destructive'>{(resources.error ?? capacity.error)!.message}</p>
        )}
        {resources.isLoading && <p className='px-4 py-8 text-sm text-muted-foreground'>Loading people…</p>}
        {resources.isSuccess && rows.length === 0 && (
          <div className='flex flex-col items-center gap-3 py-14 text-center text-sm text-muted-foreground'>
            {all.length === 0 ? 'Nobody belongs to this center yet.' : 'Nobody matches your search or team filter.'}
            {all.length === 0 && canAddPeople && (
              <Button size='sm' onClick={() => setAdding(true)}>
                <UserPlus className='mr-1.5 h-4 w-4' /> Add people from Users
              </Button>
            )}
          </div>
        )}

        {rows.length > 0 && view === 'schedule' && (
          <PeopleSchedule
            rows={rows}
            dates={dates}
            today={today}
            daysOf={daysOf}
            shifts={shifts.data ?? []}
            loading={capacity.isLoading}
            onOpen={open}
          />
        )}

        {rows.length > 0 && view === 'table' && (
          <div className='overflow-x-auto'>
            <Table>
              <TableHeader className='[&_th]:h-10 [&_th]:text-[11px] [&_th]:font-semibold [&_th]:text-white'>
                <TableRow className='hover:bg-transparent'>
                  {cols.visible.map(({ def }) => (
                    <ResizableTableHead key={def.id} columnId={def.id} columns={cols} label={def.header}>
                      {def.header}
                    </ResizableTableHead>
                  ))}
                  {dates.map(d => {
                    const h = dayHeader(d)
                    return (
                      <TableHead
                        key={d}
                        className={cn('min-w-[62px] px-1 text-center leading-tight', h.weekend && 'brightness-95', d === today && 'shadow-[inset_0_-3px_0_rgb(251_191_36)]')}
                      >
                        <span className='block text-[10px] font-medium opacity-80'>{h.dow}</span>
                        <span className='block text-xs'>{h.md}</span>
                      </TableHead>
                    )
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.userId} className={cn('group transition-colors hover:bg-muted/40', !r.enabled && 'opacity-60')}>
                    <ResourceCells
                      r={r}
                      cols={cols}
                      onOpen={() => open(r.userId)}
                      canEditGroups={canManageProfiles}
                      onEditGroups={() => setGroupsFor(r)}
                      activeTeamId={teamId}
                      onTeam={id => setTeamId(teamId === id ? null : id)}
                    />
                    {dates.map(d => (
                      <TableCell key={d} className='px-0.5 py-1.5'>
                        <DayCell day={daysOf.get(r.userId)?.find(x => x.date === d)} loading={capacity.isLoading} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      <AddPeopleDialog open={adding} onOpenChange={setAdding} memberIds={new Set(all.map(r => r.userId))} />
      <PersonGroupsDialog
        key={groupsFor?.userId ?? 'none'}
        person={groupsFor}
        canManage={canManageProfiles}
        onOpenChange={o => !o && setGroupsFor(null)}
      />
    </div>
  )
}

/** One calendar cell (slide 7): available hours, the mini-bar and the shift code. */
function DayCell({ day, loading }: { day: CapacityDay | undefined; loading: boolean }) {
  if (!day) return <div className={cn('mx-auto h-12 w-[56px] rounded-lg bg-muted/40', loading && 'animate-pulse')} />
  const v = cellView(day)
  const base = 'mx-auto flex h-12 w-[56px] flex-col items-center justify-center rounded-lg text-center'
  if (v.kind === 'not-started')
    return (
      <div className={cn(base, 'border border-dashed text-muted-foreground')} title='Before their shift starts'>
        —
      </div>
    )
  if (v.kind === 'off')
    return <div className={cn(base, 'bg-muted/70 text-[11px] font-medium tracking-wide text-muted-foreground')}>OFF</div>
  if (v.kind === 'absent')
    return (
      <div
        className={cn(base, 'border border-rose-200 text-[11px] font-semibold text-rose-700')}
        style={{ backgroundImage: ABSENCE_STRIPES }}
        title={`${v.code ?? 'Unavailable'} — the whole day`}
      >
        {v.code ?? 'UA'}
      </div>
    )
  const total = v.segments.reduce((n, s) => n + s.hrs, 0) || 1
  return (
    <div
      className={cn(base, 'relative border bg-background shadow-[0_1px_0_rgb(0_0_0/0.03)]')}
      title={`${day.shiftCode ?? ''} · ${hoursLabel(v.hours)} h available${v.uaCode ? ` · ${v.uaCode} ${hoursLabel(v.uaHrs)} h` : ''}`}
    >
      {v.uaCode && (
        <span className='absolute right-0.5 top-0.5 rounded bg-rose-100 px-0.5 text-[8px] font-bold leading-tight text-rose-700'>
          {v.uaCode}
        </span>
      )}
      <span className='text-sm font-semibold leading-none tabular-nums'>{hoursLabel(v.hours)}</span>
      <span className='mt-1 flex h-1 w-9 overflow-hidden rounded-full bg-muted'>
        {v.segments.map(s => (
          <span key={s.type} className={WORK_COLOR[s.type]} style={{ width: `${(s.hrs / total) * 100}%` }} />
        ))}
      </span>
      <span className='mt-0.5 text-[9px] font-medium uppercase tracking-wide text-muted-foreground'>{v.shiftCode}</span>
    </div>
  )
}

/** The "No team" filter: people none of whose groups is in a team. */
const NO_TEAM = '__none__'

/** One team filter pill above the table. */
function TeamPill({
  active,
  onClick,
  label,
  count,
  icon,
}: {
  active: boolean
  onClick: () => void
  label: string
  count: number
  icon?: boolean
}) {
  return (
    <button
      type='button'
      role='radio'
      aria-checked={active}
      onClick={onClick}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-all',
        active
          ? 'border-foreground bg-foreground text-background shadow-sm'
          : 'bg-background text-muted-foreground hover:border-foreground/30 hover:text-foreground',
      )}
    >
      {icon && <Network className='h-3.5 w-3.5' />}
      {label}
      <span
        className={cn(
          'rounded-full px-1.5 text-[10px] tabular-nums',
          active ? 'bg-background/20 text-background' : 'bg-muted text-muted-foreground',
        )}
      >
        {count}
      </span>
    </button>
  )
}

const AVATAR_TONES = [
  'bg-sky-100 text-sky-700',
  'bg-violet-100 text-violet-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-800',
  'bg-rose-100 text-rose-700',
  'bg-teal-100 text-teal-700',
]

/** Initials in a soft colour picked from the name, so a person keeps theirs. */
function Avatar({ name, small }: { name: string; small?: boolean }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0]!.toUpperCase())
    .join('')
  const tone = AVATAR_TONES[[...name].reduce((n, c) => n + c.charCodeAt(0), 0) % AVATAR_TONES.length]
  return (
    <span
      aria-hidden
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full font-semibold',
        small ? 'h-6 w-6 text-[10px]' : 'h-8 w-8 text-xs',
        tone,
      )}
    >
      {initials || '?'}
    </span>
  )
}

/**
 * A person's groups as chips: the Primary first with a star, then up to one
 * Secondary, the rest folded into "+N". The whole cell opens the groups popup;
 * with none yet it is an "Add group" button.
 */
function GroupChips({
  groups,
  canEdit,
  onEdit,
}: {
  groups: ResourceSummary['groups']
  canEdit: boolean
  onEdit: () => void
}) {
  if (groups.length === 0) {
    return canEdit ? (
      <button
        type='button'
        onClick={onEdit}
        className='inline-flex items-center gap-1 rounded-full border border-dashed border-primary/50 px-2.5 py-0.5 text-[11px] font-medium text-primary transition-colors hover:border-primary hover:bg-primary/5'
      >
        <Plus className='h-3 w-3' /> Add group
      </button>
    ) : (
      <span className='text-xs text-muted-foreground'>—</span>
    )
  }
  const shown = groups.slice(0, 2)
  const hidden = groups.slice(2)
  const chips = (
    <>
      {shown.map(g => (
        <span
          key={g.id}
          title={g.isPrimary ? `${g.name} · Primary group` : `${g.name} · Secondary group`}
          className={cn(
            'inline-flex max-w-[9rem] items-center gap-1 truncate rounded-full px-2 py-0.5 text-[11px]',
            g.isPrimary
              ? 'bg-primary/10 font-semibold text-primary ring-1 ring-inset ring-primary/30'
              : 'bg-muted text-foreground/80',
          )}
        >
          {g.isPrimary && <Star className='h-3 w-3 shrink-0 fill-current' />}
          <span className='truncate'>{g.name}</span>
        </span>
      ))}
      {hidden.length > 0 && (
        <span
          title={hidden.map(g => g.name).join(', ')}
          className='rounded-full bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground'
        >
          +{hidden.length}
        </span>
      )}
    </>
  )
  if (!canEdit) return <div className='flex flex-wrap items-center gap-1'>{chips}</div>
  return (
    <button
      type='button'
      onClick={onEdit}
      title='Change groups'
      className='group/chips flex flex-wrap items-center gap-1 rounded-lg p-0.5 text-left transition-colors hover:bg-muted/60'
    >
      {chips}
      <Pencil className='h-3 w-3 text-muted-foreground opacity-0 transition-opacity group-hover/chips:opacity-100' />
    </button>
  )
}

function ResourceCells({
  r,
  cols,
  onOpen,
  canEditGroups,
  onEditGroups,
  activeTeamId,
  onTeam,
}: {
  r: ResourceSummary
  cols: ReturnType<typeof useTableColumns>
  onOpen: () => void
  canEditGroups: boolean
  onEditGroups: () => void
  activeTeamId: string | null
  onTeam: (teamId: string) => void
}) {
  const shift = shiftSummary(r.shift)
  const segments = splitSegments(r.split)
  const label = splitLabel(r.split)
  return (
    <ColumnCells
      columns={cols}
      cells={{
        name: (
          <TableCell className='py-2.5'>
            <Link to={`/resources/people/${r.userId}`} className='group/name flex min-w-0 items-center gap-2.5'>
              <Avatar name={r.name} />
              <span className='min-w-0'>
                <span className='block truncate font-medium text-foreground group-hover/name:text-primary'>
                  {r.name}
                  {!r.enabled && <span className='ml-1.5 text-xs font-normal text-muted-foreground'>(disabled)</span>}
                </span>
                <span className='block truncate text-xs text-muted-foreground'>{r.email}</span>
              </span>
            </Link>
          </TableCell>
        ),
        supervisor: (
          <TableCell>
            {r.supervisor ? (
              <span className='flex min-w-0 items-center gap-2'>
                <Avatar name={r.supervisor.name} small />
                <span className='truncate text-sm'>{r.supervisor.name}</span>
              </span>
            ) : (
              <span className='text-xs text-muted-foreground'>—</span>
            )}
          </TableCell>
        ),
        teams: (
          <TableCell>
            {r.teams.length === 0 ? (
              <span className='text-xs text-muted-foreground'>—</span>
            ) : (
              <div className='flex flex-wrap gap-1'>
                {r.teams.map(t => {
                  const on = activeTeamId === t.id
                  return (
                    <button
                      key={t.id}
                      type='button'
                      onClick={() => onTeam(t.id)}
                      title={on ? 'Show all teams' : `Show only ${t.name}`}
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset transition-colors',
                        on
                          ? 'bg-indigo-600 text-white ring-indigo-600'
                          : 'bg-indigo-50 text-indigo-700 ring-indigo-200 hover:bg-indigo-100',
                      )}
                    >
                      <Network className='h-3 w-3' /> {t.name}
                    </button>
                  )
                })}
              </div>
            )}
          </TableCell>
        ),
        groupChips: (
          <TableCell>
            <GroupChips groups={r.groups} canEdit={canEditGroups} onEdit={onEditGroups} />
          </TableCell>
        ),
        split: (
          <TableCell>
            {label === null ? (
              <span className='text-xs text-muted-foreground'>Not set</span>
            ) : (
              <div className='w-24 space-y-1'>
                <div className={cn('flex h-1.5 overflow-hidden rounded-full', r.split?.isDynamic ? 'bg-sky-100' : 'bg-muted')}>
                  {segments.map(s => (
                    <span key={s.type} className={WORK_COLOR[s.type]} style={{ width: `${s.pct}%` }} />
                  ))}
                </div>
                <div className='font-mono text-[11px] text-muted-foreground'>{label}</div>
              </div>
            )}
          </TableCell>
        ),
        work: (
          <TableCell>
            <div className='flex gap-1'>
              {(r.split?.workTypes ?? []).map(t => (
                <span
                  key={t}
                  title={WORK_TYPE_CHIPS[t].title}
                  className={cn('rounded-full px-1.5 py-0.5 text-[11px] font-semibold', WORK_TYPE_CHIPS[t].className)}
                >
                  {WORK_TYPE_CHIPS[t].label}
                </span>
              ))}
              {!r.split && <span className='text-xs text-muted-foreground'>—</span>}
            </div>
          </TableCell>
        ),
        shift: (
          <TableCell>
            {shift ? (
              <button type='button' onClick={onOpen} className='text-left'>
                <div className='truncate text-primary hover:underline'>{shift.title}</div>
                <div className='truncate text-xs text-muted-foreground'>{shift.detail}</div>
              </button>
            ) : (
              <span className='text-xs text-muted-foreground'>No shift</span>
            )}
          </TableCell>
        ),
        labRole: <TableCell className='text-sm'>{r.isSupervisor ? 'Supervisor' : 'Tech'}</TableCell>,
        email: <TableCell className='truncate text-muted-foreground'>{r.email || '—'}</TableCell>,
      }}
    />
  )
}
